import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';

interface Tenant {
  id: string;
  name: string;
  type: string;
  code: string;
}

const TenantSelector: React.FC = () => {
  const { isOrgHead, selectedTenantId, setSelectedTenant, isAuthenticated } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);

  // Function to load tenants from localStorage
  const loadTenantsFromStorage = useCallback(() => {
    const tenantsFromStorage = localStorage.getItem('orgHeadTenants');
    if (tenantsFromStorage) {
      try {
        const parsedTenants = JSON.parse(tenantsFromStorage);
        setTenants(parsedTenants);
        return parsedTenants;
      } catch (error) {
        console.error('Error parsing tenants from localStorage:', error);
        return [];
      }
    }
    return [];
  }, []);

  // Function to fetch tenants if not available
  const fetchTenants = useCallback(async () => {
    if (!isOrgHead() || !isAuthenticated) return;
    
    setIsLoadingTenants(true);
    try {
      console.log('Fetching tenants for ORG_HEAD...');
      const response = await apiClient.getTenants();
      console.log('Tenants fetched:', response);
      
      const fetchedTenants = response.data?.tenants || response.tenants || [];
      setTenants(fetchedTenants);
      
      // Cache in localStorage
      localStorage.setItem('orgHeadTenants', JSON.stringify(fetchedTenants));
      
      return fetchedTenants;
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      toast.error('Failed to load tenants');
      return [];
    } finally {
      setIsLoadingTenants(false);
    }
  }, [isOrgHead, isAuthenticated]);

  // Load tenants on mount and when auth state changes
  useEffect(() => {
    if (!isOrgHead() || !isAuthenticated) {
      setTenants([]);
      return;
    }

    // First try to load from localStorage
    const storedTenants = loadTenantsFromStorage();
    
    // If no stored tenants, fetch them
    if (storedTenants.length === 0) {
      fetchTenants();
    }
  }, [isOrgHead, isAuthenticated, loadTenantsFromStorage, fetchTenants]);

  // Auto-select first tenant if none is selected and tenants are available
  useEffect(() => {
    if (tenants.length > 0 && !selectedTenantId && isOrgHead()) {
      console.log('Auto-selecting first tenant:', tenants[0]);
      setSelectedTenant(tenants[0].id);
    }
  }, [tenants, selectedTenantId, isOrgHead, setSelectedTenant]);

  const handleTenantSelect = (tenantId: string) => {
    setSelectedTenant(tenantId);
    setIsOpen(false);
    toast.success('Tenant switched successfully');
  };

  const selectedTenant = tenants.find(t => t.id === selectedTenantId);

  // Only show for ORG_HEAD users
  if (!isOrgHead()) {
    return null;
  }
  
  // If loading tenants or no tenants available yet, show loading message
  if (isLoadingTenants || (tenants.length === 0 && isAuthenticated)) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm text-yellow-800">Loading tenants...</span>
      </div>
    );
  }

  // If no tenants after loading, show empty state
  if (tenants.length === 0) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
        <Building2 className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">No tenants available</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center space-x-2 px-4 py-2 bg-white border-2 rounded-lg transition-all duration-200 hover:border-blue-400 hover:shadow-md",
          isOpen ? "border-blue-500 shadow-md" : "border-gray-200"
        )}
        disabled={false}
      >
        <Building2 className="w-5 h-5 text-blue-600" />
        <div className="flex flex-col items-start">
          <span className="text-xs text-gray-500 font-medium">Current Tenant</span>
          <span className="text-sm font-semibold text-gray-900">
            {selectedTenant?.name || 'Select Tenant'}
          </span>
        </div>
        <ChevronDown 
          className={cn(
            "w-4 h-4 text-gray-400 transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-50 overflow-hidden"
            >
              <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">Select Tenant</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Switch between tenants to view their data
                </p>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {tenants.length === 0 ? (
                  <div className="p-6 text-center">
                    <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No tenants available</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {tenants.map((tenant) => {
                      const isSelected = tenant.id === selectedTenantId;
                      return (
                        <button
                          key={tenant.id}
                          onClick={() => handleTenantSelect(tenant.id)}
                          className={cn(
                            "w-full flex items-center justify-between p-3 rounded-lg transition-all duration-150",
                            isSelected 
                              ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 shadow-sm" 
                              : "hover:bg-gray-50 border-2 border-transparent"
                          )}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center",
                              isSelected 
                                ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md" 
                                : "bg-gray-100 text-gray-600"
                            )}>
                              <Building2 className="w-5 h-5" />
                            </div>
                            <div className="flex flex-col items-start">
                              <span className={cn(
                                "text-sm font-semibold",
                                isSelected ? "text-blue-900" : "text-gray-900"
                              )}>
                                {tenant.name}
                              </span>
                              <div className="flex items-center space-x-2">
                                <span className={cn(
                                  "text-xs px-2 py-0.5 rounded-full font-medium",
                                  tenant.type === 'HYBRID' 
                                    ? "bg-purple-100 text-purple-700"
                                    : tenant.type === 'SCHOOL'
                                    ? "bg-green-100 text-green-700"
                                    : "bg-blue-100 text-blue-700"
                                )}>
                                  {tenant.type}
                                </span>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 500, damping: 30 }}
                            >
                              <Check className="w-5 h-5 text-blue-600" />
                            </motion.div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TenantSelector;
